/**
 * compress-image.ts — client-side re-encode of report photos to JPEG ≤ maxDim.
 *
 * Runs BEFORE the type/size/orientation gates in the photo panel so large phone
 * photos stop hitting the 5 MB rejection. Fail-open: any decode/canvas error
 * returns the original File unchanged (the 5 MB gate still applies to it) — a
 * photo must never be lost to a compression bug (HEIC edge cases).
 *
 * EXIF orientation: decoding via <img> (loadImage from image-frame) makes the
 * browser apply EXIF on decode; the canvas draw bakes the corrected orientation.
 */
import { loadImage } from "./image-frame.js";

const DEFAULT_MAX_DIM = 1600;
const DEFAULT_QUALITY = 0.8;

/** Scales (w, h) down so the largest side is ≤ max, preserving aspect ratio. */
export function scaledDimensions(
  w: number,
  h: number,
  max: number,
): { width: number; height: number } {
  if (w <= 0 || h <= 0) return { width: w, height: h };
  if (w <= max && h <= max) return { width: w, height: h };
  const scale = max / Math.max(w, h);
  return { width: Math.round(w * scale), height: Math.round(h * scale) };
}

/** Re-encodes a photo File to JPEG with max side `maxDim`. Fail-open on any error. */
export async function compressImage(
  file: File,
  maxDim = DEFAULT_MAX_DIM,
  quality = DEFAULT_QUALITY,
): Promise<File> {
  try {
    const img = await loadImage(file);
    const { width, height } = scaledDimensions(img.naturalWidth, img.naturalHeight, maxDim);
    if (width <= 0 || height <= 0) return file;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;

    // White-fill first so alpha channels come out white in JPEG, not black.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality),
    );
    if (!blob) return file;

    const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg" });
  } catch {
    return file; // fail-open: undecodable image → original File, gates still apply
  }
}
